import React, { useEffect, useState } from "react";
import { Badge, Button, Placeholder, Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faTrash } from "@fortawesome/free-solid-svg-icons";

import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";
import { toast } from "react-toastify";

const MailTemplateList = ({ ...props }) => {
  const {
    workspaceId,
    page,
    setCount,
    limit,
    loading,
    setLoading,
    setSelectedTemplate,
    setPopupShow,
    reload,
  } = props;

  const [templateList, setTemplateList] = useState([]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await Utils._apiRequest({
          path: Enums.endpoints.mailtemplate.ALL,
          type: "GET",
          query: { workspace: workspaceId, page, limit },
        });
        setLoading(false);
        setTemplateList(response.data);
        setCount(response.count);
      } catch (error) {
        setLoading(false);
        console.log("Failed to fetch mail templates:", error.message);
      }
    };

    setLoading(true);
    fetchTemplates();
  }, [workspaceId, page, limit, setCount, setLoading, reload]);

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setPopupShow(true);
  };

  const handleDelete = async (templateId) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        await Utils._apiRequest({
          path: `${Enums.endpoints.mailtemplate.ALL}/${templateId}`,
          type: "DELETE",
        });
        toast.success("Template deleted successfully");
        setTemplateList(templateList.filter((t) => t._id !== templateId));
      } catch (error) {
        toast.error(error.message || "Failed to delete template");
      }
    }
  };

  return (
    <OverlayScrollbarsComponent
      style={{ maxWidth: "100%" }}
      options={{ scrollbars: { autoHide: "scroll" } }}
    >
      <Table striped hover className="table-text-inline mail-template-table">
        <thead>
          <tr>
            <th style={{ width: 20 }}>#</th>
            <th>Name</th>
            <th>Category</th>
            <th>Languages</th>
            <th>Owner</th>
            <th style={{ width: 140 }} className="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <>
              {[...Array(limit).keys()].map((i) => {
                return (
                  <tr key={i} className="placeholder-group">
                    <td>{i + 1}</td>
                    <td>
                      <Placeholder animation="glow">
                        <Placeholder xs={10} />
                      </Placeholder>
                    </td>
                    <td>
                      <Placeholder animation="glow">
                        <Placeholder xs={8} />
                      </Placeholder>
                    </td>
                    <td>
                      <Placeholder animation="glow">
                        <Placeholder xs={6} />
                      </Placeholder>
                    </td>
                    <td>
                      <Placeholder animation="glow">
                        <Placeholder xs={8} />
                      </Placeholder>
                    </td>
                    <td className="text-inline text-end">
                      <Button
                        className="ms-1"
                        size="sm"
                        disabled
                        variant="secondary"
                      ></Button>
                    </td>
                  </tr>
                );
              })}
            </>
          ) : (
            <>
              {templateList && templateList.length > 0 ? (
                templateList.map((template, i) => {
                  return (
                    <tr key={template._id || i}>
                      <td>{(page - 1) * limit + i + 1}</td>
                      <td>
                        <div className="mail-template-table__name-cell">
                          <strong>{template.name}</strong>
                        </div>
                      </td>
                      <td>
                        {template.category ? (
                          <Badge className="mail-template-table__category">{template.category}</Badge>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        {template.content?.length > 0 ? (
                          <div className="mail-template-table__languages">
                            {template.content
                              .map((c) => c.lang)
                              .filter(Boolean)
                              .map((lang, langIndex) => (
                                <span className="mail-template-table__lang-pill" key={`${template._id}-${lang}-${langIndex}`}>
                                  {lang}
                                </span>
                              ))}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        {(() => {
                          const displayName =
                            template.owner?.fullName ||
                            template.owner?.email ||
                            "Unknown User";
                          const initial = displayName.charAt(0).toUpperCase();
                          const bgColor = Utils.stringToColor(displayName);

                          return (
                            <div className="mail-template-table__owner">
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`tooltip-${template._id}`}>
                                    {displayName}
                                  </Tooltip>
                                }
                              >
                                <div
                                  className="rounded-circle text-white d-flex align-items-center justify-content-center mail-template-table__owner-avatar"
                                  style={{
                                    backgroundColor: bgColor,
                                  }}
                                >
                                  {initial}
                                </div>
                              </OverlayTrigger>
                              <span className="mail-template-table__owner-name">{displayName}</span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="text-inline text-end">
                        <div className="mail-template-table__actions">
                          <Button
                            className="ms-1"
                            size="sm"
                            variant="outline-secondary"
                            title="Edit"
                            onClick={() => handleEdit(template)}
                          >
                            <FontAwesomeIcon icon={faPenToSquare} />
                          </Button>
                          <Button
                            className="ms-1"
                            size="sm"
                            variant="outline-danger"
                            title="Delete"
                            onClick={() => handleDelete(template._id)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6}>
                    <div className="mail-template-table__empty">
                      <i>No templates found for this workspace.</i>
                    </div>
                  </td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </Table>
    </OverlayScrollbarsComponent>
  );
};

export default MailTemplateList;
